import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, AlertTriangle, Shield, FileText, Users, BookOpen, Award, Target } from 'lucide-react';

const standardCategories = [
  {
    icon: Target,
    title: 'Educational Objectives',
    criteria: [
      'Clearly defined learning objectives aligned with professional practice',
      'Measurable outcomes linked to competency development',
      'Appropriate scope for the target audience and level of practice',
    ],
  },
  {
    icon: BookOpen,
    title: 'Curriculum Structure',
    criteria: [
      'Logical sequencing of content and learning activities',
      'Balanced integration of theoretical and practical components',
      'Appropriate depth relative to the stated learning hours',
    ],
  },
  {
    icon: FileText,
    title: 'Educational Design',
    criteria: [
      'Evidence-based content supported by current scientific literature',
      'Active learning methodologies and participant engagement strategies',
      'Appropriate use of instructional technology and learning resources',
    ],
  },
  {
    icon: Users,
    title: 'Faculty Qualifications',
    criteria: [
      'Faculty members hold relevant professional qualifications',
      'Demonstrated subject matter expertise in the educational area',
      'Appropriate faculty-to-participant ratios for practical components',
    ],
  },
  {
    icon: Shield,
    title: 'Assessment & Quality',
    criteria: [
      'Participant assessment methods aligned with learning objectives',
      'Quality assurance processes for ongoing program improvement',
      'Participant feedback mechanisms and evaluation systems',
    ],
  },
  {
    icon: Award,
    title: 'Learning Outcomes',
    criteria: [
      'Documented evidence of participant learning achievement',
      'Post-activity evaluation of knowledge and skill acquisition',
      'Measurement of educational impact on professional practice',
    ],
  },
];

export default function CPDRecognitionStandardsPage() {
  return (
    <div data-testid="cpd-recognition-standards-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">CPD Recognition</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Recognition Standards</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            IMCAC evaluates educational activities against defined quality standards to determine eligibility for CPD Recognition.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC CPD Recognition evaluates educational activities for Continuing Professional Development purposes only. Recognition does not constitute governmental approval, university accreditation, specialist recognition, professional licensure, degree granting authority, or regulatory endorsement.
            </p>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">Quality Standards Framework</h2>
            <p className="text-base text-slate-600 mt-3 max-w-2xl mx-auto">
              Educational activities submitted for CPD Recognition are evaluated across six core quality dimensions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {standardCategories.map((cat, i) => (
              <Card key={i} className="imcac-card border-t-2 border-t-[#C5A572]" data-testid={`standard-${i}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#1B2A4A] rounded-sm flex items-center justify-center mb-4">
                    <cat.icon className="h-6 w-6 text-[#C5A572]" />
                  </div>
                  <h3 className="text-base font-semibold text-[#1B2A4A] font-heading mb-3">{cat.title}</h3>
                  <div className="space-y-2">
                    {cat.criteria.map((item, j) => (
                      <div key={j} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
